import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Accessibility, X, RotateCcw } from 'lucide-react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    highlightLinks: false,
    bigCursor: false
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) {
      const parsedSettings = JSON.parse(saved);
      setSettings(parsedSettings);
      applySettings(parsedSettings);
    }
  }, []);

  // Apply settings to document
  const applySettings = (newSettings) => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${newSettings.fontSize}%`;
    
    // High contrast
    if (newSettings.highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    // Highlight links
    if (newSettings.highlightLinks) {
      document.body.classList.add('highlight-links');
    } else {
      document.body.classList.remove('highlight-links');
    }
    
    // Big cursor
    if (newSettings.bigCursor) {
      document.body.classList.add('big-cursor');
    } else {
      document.body.classList.remove('big-cursor');
    }
  };

  // Update settings and save to localStorage
  const updateSettings = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
    applySettings(newSettings);
  };

  // Font size controls
  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSettings('fontSize', settings.fontSize - 10);
    }
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSettings('fontSize', settings.fontSize + 10);
    }
  };

  // Reset all settings
  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      highlightLinks: false,
      bigCursor: false
    };
    setSettings(defaultSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
    applySettings(defaultSettings);
  };

  return (
    <>
      {/* Accessibility Styles */}
      <style>{`
        .high-contrast {
          filter: contrast(1.25) !important;
        }
        .high-contrast * {
          border-color: #000 !important;
        }
        .highlight-links a {
          background-color: #ffff00 !important;
          color: #000 !important;
          text-decoration: underline !important;
          padding: 2px 4px !important;
        }
        .big-cursor,
        .big-cursor * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='black' stroke='white' stroke-width='1'%3E%3Cpath d='M4 4l16 8-8 2-2 8z'/%3E%3C/svg%3E") 0 0, auto !important;
        }
      `}</style>

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        aria-label="Accessibility Options"
        data-testid="accessibility-toggle"
      >
        <Accessibility size={28} />
      </button>

      {/* Widget Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 left-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          role="dialog"
          aria-label="Accessibility Settings"
        >
          {/* Header */}
          <div className="bg-blue-600 text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility size={22} />
              <span className="font-bold text-lg">Accessibility</span>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="hover:bg-blue-700 p-1 rounded-full transition-colors"
              aria-label="Close accessibility panel"
            >
              <X size={20} />
            </button>
          </div>

          {/* Controls */}
          <div className="p-5 space-y-5">
            {/* Font Size */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Font Size
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={decreaseFontSize}
                  disabled={settings.fontSize <= 80}
                  className="w-12 h-12 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-bold text-lg transition-colors"
                  aria-label="Decrease font size"
                >
                  A-
                </button>
                <div className="flex-1 text-center">
                  <span className="text-2xl font-bold text-blue-600">{settings.fontSize}%</span>
                </div>
                <button
                  onClick={increaseFontSize}
                  disabled={settings.fontSize >= 150}
                  className="w-12 h-12 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-bold text-lg transition-colors"
                  aria-label="Increase font size"
                >
                  A+
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">High Contrast</span>
              <button
                onClick={() => updateSettings('highContrast', !settings.highContrast)}
                className={`w-14 h-8 rounded-full transition-colors ${
                  settings.highContrast ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={settings.highContrast}
                aria-label="Toggle high contrast"
              >
                <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  settings.highContrast ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Highlight Links */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Highlight Links</span>
              <button
                onClick={() => updateSettings('highlightLinks', !settings.highlightLinks)}
                className={`w-14 h-8 rounded-full transition-colors ${
                  settings.highlightLinks ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={settings.highlightLinks}
                aria-label="Toggle highlight links"
              >
                <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  settings.highlightLinks ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Big Cursor */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Big Cursor</span>
              <button
                onClick={() => updateSettings('bigCursor', !settings.bigCursor)}
                className={`w-14 h-8 rounded-full transition-colors ${
                  settings.bigCursor ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={settings.bigCursor}
                aria-label="Toggle big cursor"
              >
                <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  settings.bigCursor ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Reset Button */}
            <button
              onClick={resetSettings}
              className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
              aria-label="Reset all accessibility settings"
            >
              <RotateCcw size={18} />
              Reset Settings
            </button>
          </div>

          {/* Footer Link */}
          <div className="border-t border-gray-200 px-5 py-3 bg-gray-50">
            <Link
              to="/website-accessibility"
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline flex items-center justify-center gap-1"
              onClick={() => setIsOpen(false)}
            >
              <Accessibility size={14} />
              Accessibility Statement
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
